import {
    FormEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import { AssignDeviceRequest, Device } from '@/api';
import { Trans } from 'react-i18next';
import {
    UIAnimation,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISelectInput,
    UISelectValue,
    UIText
} from '@/ui';
import { blue } from '@/ui/styles';
import checkedDone from '../assets/lotties/checked-done.json';
import { useMyCompanyUserFor } from '@/companies/hooks';
import { useAssignDeviceAction } from '../actions';
import { useDevicesTranslation } from '../hooks';

type AssignDeviceModalProps = {
    device: Device;
    isOpen: boolean;
    onClose: () => void;
};

export function AssignDeviceModal(props: AssignDeviceModalProps): ReactElement {
    const { device, isOpen, onClose } = props;

    const deviceId = device.id;
    const companyId = device.companyId ?? '';

    const { assignDevice, loading } = useAssignDeviceAction({
        deviceId
    });

    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    useEffect(() => {
        if (!isOpen) {
            setShowSuccessMessage(false);
        }
    }, [isOpen]);

    // Scoped to the device's company, not the selected one — this modal is
    // reachable from the standalone device page, which a QR code can open for
    // any company the user belongs to.
    const myCompanyUser = useMyCompanyUserFor(companyId);

    // A device can only be assigned to a team I am in, and my own company user
    // already lists exactly those with their names.
    const availableTeams = useMemo(() => {
        return (myCompanyUser?.teams ?? []).map((team) => ({
            id: team.teamId,
            name: team.name
        }));
    }, [myCompanyUser]);

    function handleClose() {
        onClose();
    }

    function onSubmit(data: AssignDeviceRequest) {
        assignDevice(data)
            .then(() => {
                setShowSuccessMessage(true);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            <AssignDeviceModalForm
                isOpen={isOpen}
                showSuccessMessage={showSuccessMessage}
                onClose={handleClose}
                loading={loading}
                onSubmit={onSubmit}
                teams={availableTeams}
            />
        </UIModal>
    );
}

type AssignDeviceModalFormProps = {
    isOpen: boolean;
    showSuccessMessage: boolean;
    /** The teams the caller may assign to — id and display name only. */
    teams: { id: string; name: string }[];
    loading: boolean;
    onClose: () => void;
    onSubmit: (data: AssignDeviceRequest) => void;
};

function AssignDeviceModalForm(
    props: AssignDeviceModalFormProps
): ReactElement {
    const { isOpen } = props;

    if (isOpen) {
        return <FormContent {...props} />;
    } else {
        return <></>;
    }
}

function FormContent(props: AssignDeviceModalFormProps): ReactElement {
    const { showSuccessMessage, teams, loading, onClose, onSubmit } = props;
    const { t } = useDevicesTranslation();
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Team
     */
    const [team, setTeam] = useState<UISelectValue | undefined>(undefined);
    const [teamError, setTeamError] = useState('');
    const teamOptions: UISelectValue[] = useMemo(
        () =>
            teams.map((team) => ({
                value: team.id,
                label: `${team.name}`
            })),
        [teams]
    );
    // No membership check against the options: the list is built from my own
    // teams, so every option is one I may assign to by construction. The
    // server rejects anything else.
    const checkTeam = useCallback(
        (selectedTeam: UISelectValue | undefined): string => {
            if (!selectedTeam) {
                return t('shared.fields.team.error_required');
            }

            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setTeamError(checkTeam(team));
        }
    }, [team, showRealTimeErrors, checkTeam]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkTeam(team)) {
            setShowRealTimeErrors(false);
            onSubmit?.({
                teamId: team!.value
            });
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit} noValidate>
                {showSuccessMessage ? (
                    <>
                        <UIModalContent>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 16
                                }}
                            >
                                <div
                                    style={{
                                        height: 150,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <UIAnimation
                                        animationData={checkedDone}
                                        loop={false}
                                        style={{
                                            height: 400
                                        }}
                                    />
                                </div>
                                <UIText variant="h2" bold>
                                    {t(
                                        'components.assign_device_modal.success_title'
                                    )}
                                </UIText>
                                <UIText textAlign="center">
                                    {t(
                                        'components.assign_device_modal.success_close_message'
                                    )}
                                </UIText>
                                <UIText textAlign="center">
                                    <Trans
                                        t={t}
                                        i18nKey="components.assign_device_modal.success_instructions"
                                        components={{
                                            link: (
                                                <a
                                                    href="https://www.safepointapp.com"
                                                    target="_blank"
                                                    style={{ color: blue }}
                                                />
                                            )
                                        }}
                                    />
                                </UIText>
                            </div>
                        </UIModalContent>
                        <UIModalFooter
                            actions={[
                                {
                                    text: t('shared.labels.close'),
                                    disabled: loading,
                                    onClick: onClose
                                }
                            ]}
                        />
                    </>
                ) : (
                    <>
                        <UIModalHeader
                            title={t('components.assign_device_modal.title')}
                        />
                        <UIModalContent>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 24
                                }}
                            >
                                <UISelectInput
                                    label={t('shared.fields.team.label')}
                                    placeholder={t(
                                        'shared.fields.team.placeholder'
                                    )}
                                    description={t(
                                        'shared.devices.team_description'
                                    )}
                                    required
                                    options={teamOptions}
                                    value={team}
                                    onChange={setTeam}
                                    errorText={
                                        showRealTimeErrors ? teamError : ''
                                    }
                                />
                            </div>
                        </UIModalContent>
                        <UIModalFooter
                            actions={[
                                {
                                    variant: 'primary',
                                    type: 'submit',
                                    loading: loading,
                                    disabled: loading,
                                    text: t('shared.labels.submit')
                                },
                                {
                                    text: t('shared.labels.cancel'),
                                    disabled: loading,
                                    onClick: onClose
                                }
                            ]}
                        />
                    </>
                )}
            </form>
        </>
    );
}
